import { CollapsibleFilters } from '../components/common/CollapsibleFilters';
import { SavedBillingViews } from '../components/billing/BillingWorkflowTools'
import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Stack,
  Input,
  Select,
  Option,
  Checkbox,
  CircularProgress,
  Alert,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
} from '@mui/joy'
import {
  Plus as Add,
  MagnifyingGlass as Search,
  Envelope as Email,
  DotsThreeVertical as MoreVert,
  Trash as Delete,
  PencilSimple as Edit,
  Eye as Visibility,
  Printer as Print,
  CheckSquare as CheckBox,
  CurrencyDollar as AttachMoney,
  Invoice as RequestQuote,
} from '@phosphor-icons/react'
import { invoicesApi } from '../lib/billingApi'
import { formatCurrency, getStatusColor, formatStatus } from '../lib/billingUtils'
import { ResponsiveTable } from '../components/common/ResponsiveTable'
import type { Invoice, InvoiceFilters, DocumentStatus } from '../types/billing'

export default function InvoiceList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Filter state
  const [filters, setFilters] = useState<InvoiceFilters>({
    status: searchParams.get('status') as DocumentStatus || undefined,
    search: searchParams.get('search') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    overdue_only: searchParams.get('overdue_only') === 'true',
  })

  // Selection state
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([])
  const [bulkStatusModal, setBulkStatusModal] = useState(false)
  const [bulkStatus, setBulkStatus] = useState<DocumentStatus>('sent')
  useEffect(() => {
    setFilters({ ...Object.fromEntries(searchParams),
      status: searchParams.get('status') || undefined,
      search: searchParams.get('search') || '',
      date_from: searchParams.get('date_from') || '', date_to: searchParams.get('date_to') || '',
      overdue_only: searchParams.get('overdue_only') === 'true',
      expired_only: searchParams.get('expired_only') === 'true',
    } as any)
    setCurrentPage(1)
  }, [searchParams])
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 20

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters.status, filters.search, filters.date_from, filters.date_to, filters.overdue_only])

  // Fetch invoices
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', filters, currentPage],
    queryFn: () => invoicesApi.list({ ...filters, page: currentPage }),
  })

  // Fetch summary
  const { data: summary } = useQuery({
    queryKey: ['invoice-summary', filters],
    queryFn: () => invoicesApi.summary(filters),
  })

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: number[], status: DocumentStatus }) =>
      invoicesApi.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoice-summary'] })
      setSelectedInvoices([])
      setBulkStatusModal(false)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => invoicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoice-summary'] })
      toast.success('Invoice deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice')
    },
  })

  // Email mutation
  const emailMutation = useMutation({
    mutationFn: (id: number) => invoicesApi.sendEmail(id),
  })

  // Bulk email state
  const [bulkEmailSending, setBulkEmailSending] = useState(false)

  const handleBulkSendEmail = async () => {
    if (selectedInvoices.length === 0) return
    setBulkEmailSending(true)
    try {
      const results = await Promise.allSettled(
        selectedInvoices.map((id) => invoicesApi.sendEmail(id))
      )
      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.length - succeeded

      if (failed === 0) {
        toast.success(`Sent ${succeeded} invoice${succeeded > 1 ? 's' : ''} by email`)
        setSelectedInvoices([])
      } else if (succeeded === 0) {
        toast.error(`Failed to send ${failed} invoice${failed > 1 ? 's' : ''}`)
      } else {
        toast.error(`Sent ${succeeded}, but ${failed} failed`)
      }
    } finally {
      setBulkEmailSending(false)
    }
  }

  const handleFilterChange = (newFilters: Partial<InvoiceFilters>) => {
    const updatedFilters = { ...filters, ...newFilters }
    setFilters(updatedFilters)

    // Update URL params
    const params = new URLSearchParams()
    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value) params.set(key, String(value))
    })
    setSearchParams(params)
  }

  const handleSelectAll = () => {
    if (selectedInvoices.length === data?.results.length) {
      setSelectedInvoices([])
    } else {
      setSelectedInvoices(data?.results.map(inv => inv.id) || [])
    }
  }

  const handleSelectInvoice = (id: number) => {
    setSelectedInvoices(prev =>
      prev.includes(id)
        ? prev.filter(invId => invId !== id)
        : [...prev, id]
    )
  }


  if (error) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Error loading invoices. Please try again.
      </Alert>
    )
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 1.5, md: 3 } }}>
      <SavedBillingViews kind="invoices"/>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: { xs: 3, md: 4 }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box>
          <Typography level="h3">Invoices</Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            Track and manage customer invoices
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button
            component={Link}
            to="/operations/billing/payments"
            variant="outlined"
            color="primary"
            startDecorator={<AttachMoney />}
          >
            View Payments
          </Button>
          <Button
            component={Link}
            to="../estimates"
            variant="outlined"
            color="primary"
            startDecorator={<RequestQuote />}
          >
            View Estimates
          </Button>
          <Button
            component={Link}
            to="new"
            variant="solid"
            color="primary"
            startDecorator={<Add />}
          >
            New Invoice
          </Button>
        </Stack>
      </Stack>

      {/* Summary Cards */}
      {summary && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
            <Card variant="soft" color="primary">
              <CardContent>
                <Typography level="body-sm">Total Amount</Typography>
                <Typography level="h3">{formatCurrency(summary?.total_amount)}</Typography>
                <Typography level="body-sm">{summary?.invoice_count || 0} invoices</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="soft" color="success">
              <CardContent>
                <Typography level="body-sm">Paid</Typography>
                <Typography level="h3">{formatCurrency(summary?.paid_amount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="soft" color="warning">
              <CardContent>
                <Typography level="body-sm">Outstanding</Typography>
                <Typography level="h3">{formatCurrency(summary?.outstanding_amount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="soft" color="danger">
              <CardContent>
                <Typography level="body-sm">Overdue</Typography>
                <Typography level="h3">{formatCurrency(summary?.overdue_amount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
      )}

      {/* Filters */}
      <CollapsibleFilters><Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControl size="sm" sx={{ minWidth: 200 }}>
              <Input
                placeholder="Search invoices..."
                value={filters.search}
                onChange={(e) => handleFilterChange({ search: e.target.value })}
                startDecorator={<Search />}
              />
            </FormControl>

            <FormControl size="sm" sx={{ minWidth: 150 }}>
              <Select
                placeholder="Status"
                value={filters.status}
                onChange={(_, value) => handleFilterChange({ status: value as DocumentStatus })}
              >
                <Option value="">All Statuses</Option>
                <Option value="draft">Draft</Option>
                <Option value="sent">Sent</Option>
                <Option value="viewed">Viewed</Option>
                <Option value="paid">Paid</Option>
                <Option value="partially_paid">Partially Paid</Option>
                <Option value="overdue">Overdue</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </FormControl>

            <FormControl size="sm">
              <Input
                type="date"
                placeholder="From date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange({ date_from: e.target.value })}
              />
            </FormControl>

            <FormControl size="sm">
              <Input
                type="date"
                placeholder="To date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange({ date_to: e.target.value })}
              />
            </FormControl>

            <Checkbox
              label="Overdue only"
              checked={filters.overdue_only}
              onChange={(e) => handleFilterChange({ overdue_only: e.target.checked })}
              size="sm"
            />

            <Button
              variant="outlined"
              color="primary"
              size="sm"
              onClick={() => {
                setFilters({})
                setSearchParams(new URLSearchParams())
              }}
            >
              Clear Filters
            </Button>
          </Stack>
        </CardContent>
      </Card></CollapsibleFilters>

      {/* Bulk Actions */}
      {selectedInvoices.length > 0 && (
        <Alert
          color="primary"
          variant="soft"
          sx={{ mb: 2 }}
          startDecorator={<CheckBox />}
          endDecorator={
            <Stack direction="row" spacing={1}>
              <Button
                size="sm"
                variant="solid"
                onClick={() => setBulkStatusModal(true)}
              >
                Update Status
              </Button>
              <Button
                size="sm"
                variant="outlined"
                color="primary"
                startDecorator={<Email />}
                loading={bulkEmailSending}
                onClick={handleBulkSendEmail}
              >
                Send Email
              </Button>
            </Stack>
          }
        >
          {selectedInvoices.length} invoice{selectedInvoices.length > 1 ? 's' : ''} selected
        </Alert>
      )}

      {/* Invoice Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data?.results && data.results.length > 0 ? (
        <ResponsiveTable<Invoice>
          rows={data.results}
          getRowKey={(invoice) => invoice.id}
          columns={[
            {
              key: 'select',
              header: (
                <Checkbox
                  checked={selectedInvoices.length === data.results.length && data.results.length > 0}
                  indeterminate={selectedInvoices.length > 0 && selectedInvoices.length < data.results.length}
                  onChange={handleSelectAll}
                />
              ),
              mobileLabel: 'Select',
              width: '50px',
              render: (invoice) => (
                <Checkbox
                  checked={selectedInvoices.includes(invoice.id)}
                  onChange={() => handleSelectInvoice(invoice.id)}
                />
              ),
            },
            {
              key: 'document_number',
              sortValue: (row) => row.document_number,
              header: 'Invoice #',
              mobileVariant: 'header',
              render: (invoice) => (
                <Link to={`${invoice.id}`}>
                  <Typography level="title-sm">{invoice.document_number}</Typography>
                </Link>
              ),
            },
            {
              key: 'customer',
              sortValue: (row) => row.customer_detail?.last_name ?? row.customer_detail?.first_name,
              header: 'Customer',
              render: (invoice) => (
                <Box>
                  <Typography level="body-sm">
                    {invoice.customer_detail
                      ? `${invoice.customer_detail.first_name} ${invoice.customer_detail.last_name}`
                      : 'N/A'}
                  </Typography>
                  {invoice.job_name && (
                    <Typography level="body-xs" color="neutral">
                      {invoice.job_name}
                    </Typography>
                  )}
                </Box>
              ),
            },
            {
              key: 'date',
              sortValue: (row) => row.document_date ? new Date(row.document_date) : null,
              header: 'Date',
              render: (invoice) => (
                <Typography level="body-sm">
                  {new Date(invoice.document_date).toLocaleDateString()}
                </Typography>
              ),
            },
            {
              key: 'due_date',
              sortValue: (row) => row.due_date ? new Date(row.due_date) : null,
              header: 'Due Date',
              render: (invoice) => (
                <Box>
                  <Typography level="body-sm">
                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                  </Typography>
                  {invoice.days_overdue && invoice.days_overdue > 0 && (
                    <Typography level="body-xs" color="danger">
                      {invoice.days_overdue} days overdue
                    </Typography>
                  )}
                </Box>
              ),
            },
            {
              key: 'amount',
              sortValue: (row) => Number(row.total_amount),
              header: 'Amount',
              render: (invoice) => (
                <Typography level="body-sm" fontWeight="lg">
                  {formatCurrency(invoice.total_amount)}
                </Typography>
              ),
            },
            {
              key: 'balance',
              sortValue: (row) => Number(row.balance_due),
              header: 'Balance',
              render: (invoice) => (
                <Typography
                  level="body-sm"
                  fontWeight="lg"
                  color={parseFloat(invoice.balance_due || '0') > 0 ? 'warning' : 'success'}
                >
                  {formatCurrency(invoice.balance_due || 0)}
                </Typography>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (invoice) => (
                <Chip size="sm" color={getStatusColor(invoice.status)} variant="soft">
                  {formatStatus(invoice.status)}
                </Chip>
              ),
            },
          ]}
          rowActions={(invoice) => (
            <Dropdown>
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{ root: { variant: 'plain', size: 'sm' } }}
              >
                <MoreVert />
              </MenuButton>
              <Menu size="sm" placement="bottom-end">
                <MenuItem component={Link} to={`${invoice.id}`}>
                  <Visibility /> View
                </MenuItem>
                <MenuItem component={Link} to={`${invoice.id}/edit`}>
                  <Edit /> Edit
                </MenuItem>
                <MenuItem onClick={() => emailMutation.mutate(invoice.id)}>
                  <Email /> Send Email
                </MenuItem>
                <MenuItem component={Link} to={`../payments/new?invoice=${invoice.id}`}>
                  <AttachMoney /> Record Payment
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    invoicesApi
                      .generatePdf(invoice.id)
                      .then((data) => {
                        window.open(data.pdf_url, '_blank')
                        toast.success('PDF opened in new tab')
                      })
                      .catch(() => toast.error('Failed to generate PDF'))
                  }}
                >
                  <Print /> Print
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this invoice?')) {
                      deleteMutation.mutate(invoice.id)
                    }
                  }}
                  sx={{ color: 'danger.main' }}
                >
                  <Delete /> Delete
                </MenuItem>
              </Menu>
            </Dropdown>
          )}
        />
      ) : (
        <Card variant="soft">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography level="body-lg" sx={{ mb: 2 }}>No invoices found</Typography>
            <Button
              component={Link}
              to="new"
              startDecorator={<Add />}
              variant="solid"
            >
              Create Your First Invoice
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mt: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Typography level="body-sm" color="neutral">
          {(data?.count ?? 0) > 0
            ? `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, data!.count)}–${Math.min(currentPage * PAGE_SIZE, data!.count)} of ${data!.count} invoices`
            : 'No invoices'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage >= Math.ceil((data?.count ?? 0) / PAGE_SIZE)}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </Button>
        </Box>
      </Stack>

      {/* Bulk Status Update Modal */}
      <Modal open={bulkStatusModal} onClose={() => setBulkStatusModal(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Update Invoice Status</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ mb: 2 }}>
              Update status for {selectedInvoices.length} selected invoice{selectedInvoices.length > 1 ? 's' : ''}
            </Typography>
            <FormControl>
              <FormLabel>New Status</FormLabel>
              <Select value={bulkStatus} onChange={(_, value) => setBulkStatus(value as DocumentStatus)}>
                <Option value="draft">Draft</Option>
                <Option value="sent">Sent</Option>
                <Option value="paid">Paid</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setBulkStatusModal(false)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              onClick={() => bulkUpdateMutation.mutate({ ids: selectedInvoices, status: bulkStatus })}
              loading={bulkUpdateMutation.isPending}
            >
              Update Status
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  )
}

// Grid component (since Joy UI doesn't have Grid, we'll use Box with flex)
const Grid: React.FC<{
  children: React.ReactNode
  container?: boolean
  spacing?: number
  xs?: number
  sm?: number
  md?: number
  lg?: number
}> = ({ children, container, spacing = 0, xs, sm, md, lg }) => {
  if (container) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          margin: spacing ? -spacing / 2 : 0,
        }}
      >
        {children}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        padding: spacing ? spacing / 2 : 0,
        width: {
          xs: xs ? `${(xs / 12) * 100}%` : '100%',
          sm: sm ? `${(sm / 12) * 100}%` : undefined,
          md: md ? `${(md / 12) * 100}%` : undefined,
          lg: lg ? `${(lg / 12) * 100}%` : undefined,
        },
      }}
    >
      {children}
    </Box>
  )
}
